/* eslint-disable no-unused-vars */
import { useTrackingStore } from '@/stores/tracking/trackingStore';
import { useSessionStatusStore } from '@/stores/sessionStatusStore';
import {
  ensureCheckoutFlowStarted,
  trackStatusViewed,
  trackPaymentStarted,
  trackPaymentPageViewed,
  trackVerificationStarted,
  trackVerificationOtpRequested,
} from '@/composables/tracking/useConversionTracking';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';
import { getOrderData } from '@/composables/api/useTracker';

import useApiHelpers from '@/composables/api/helpers';
import { resetFraudSid } from '@/composables/api/useFraudSession';
import usePaymentEndpoints from '@/composables/api/usePaymentEndpoints';
import useModalActions from '@/composables/store/modal/useModalActions';
import useProgressBarActions from '@/composables/progress/useProgressBarActions';
import { expressCheckoutEnabled } from '@/composables/router/handleExpressCheckout';

const progressBarActions = () => useProgressBarActions();
const paymentEndpoints = () => usePaymentEndpoints();
const modalActions = () => useModalActions();
const apiHelpers = () => useApiHelpers();

const resolvePspTypeForTracking = (to) => {
  const fromUrl = to.params?.payment;
  if (fromUrl) {
    return String(fromUrl).toLowerCase();
  }
  const paymentMethod = getOrderData()?.paymentMethod;
  return paymentMethod ? String(paymentMethod).toLowerCase() : 'unknown';
};

const startCheckoutFlowForLanding = (entryPoint) => {
  const paymentMethod = getOrderData()?.paymentMethod;
  ensureCheckoutFlowStarted({
    entryPoint,
    paymentMethod: paymentMethod ? String(paymentMethod).toLowerCase() : 'unknown',
    flowVersion: '1.0',
  });
};

const routes = [
  {
    path: '/',
    name: 'order',
    component: () => import('@/views/OrderView.vue'),
    beforeEnter(to, from, next) {
      progressBarActions().resetAllProgress();
      // Vue Router: pass previous route to view for handleApiRedirect safety checks
      // eslint-disable-next-line no-param-reassign
      to.meta = { ...to.meta, fromRoute: from ? { name: from.name } : undefined };
      startCheckoutFlowForLanding('order');
      next();
    },
  },
  {
    path: '/recovery/:orderId?',
    name: 'order-recovery',
    component: () => import('@/views/OrderRecoveryView.vue'),
    beforeEnter(to, from, next) {
      if (to.params.orderId) {
        const trackingStore = useTrackingStore();
        trackingStore.recordOrderKey(to.params.orderId);
      }
      startCheckoutFlowForLanding('recovery');
      modalActions().toggleOrderRecoveryModal();
      progressBarActions().resetAllProgress();
      next();
    },
  },
  {
    path: '/recovery/opt-out/:orderId?',
    name: 'order-recovery.opt-out',
    component: () => import('@/views/OrderRecoveryOptOutView.vue'),
  },
  {
    path: '/papi/transit/',
    name: 'order.papi',
    component: () => import('@/views/OrderView.vue'),
    beforeEnter(to, from, next) {
      progressBarActions().resetAllProgress();
      // Vue Router: pass previous route to view for handleApiRedirect safety checks
      // eslint-disable-next-line no-param-reassign
      to.meta = { ...to.meta, fromRoute: from ? { name: from.name } : undefined };
      next();
    },
  },
  {
    path: '/verification',
    name: 'verification',
    component: () => import('@/views/VerificationView.vue'),
    beforeEnter(to, from, next) {
      trackVerificationStarted();
      progressBarActions().updateToPending('verification');
      next();
    },
  },
  {
    path: '/verification/v2',
    name: 'verificationV2',
    component: () => import('@/views/VerificationViewV2.vue'),
    beforeEnter(to, from, next) {
      trackVerificationStarted();
      progressBarActions().updateToPending('verification');
      next();
    },
  },
  {
    path: '/verification/code',
    name: 'verificationCode',
    component: () => import('@/views/VerificationCodeView.vue'),
    beforeEnter(to, from, next) {
      trackVerificationStarted();
      trackVerificationOtpRequested();
      next();
    },
  },
  {
    path: '/payment',
    name: 'payment',
    component: () => import('@/views/payment/PaymentView.vue'),
    beforeEnter(to, from, next) {
      const pspType = resolvePspTypeForTracking(to);
      trackPaymentStarted({ psp_type: pspType });
      trackPaymentPageViewed({ psp_type: pspType });
      next();
    },
  },
  {
    path: '/payment/plaid',
    name: 'payment.plaid.relink',
    component: () => import('@/views/plaidRedirect/PlaidRedirectView.vue'),
    async beforeEnter(to, from, next) {
      progressBarActions().updateToComplete(['verification', 'identity']);
      progressBarActions().updateToPending('payment');
      next();
    },
    children: [
      {
        path: 'relink',
        component: () => import('@/views/plaidRedirect/OpenBankingView.vue'),
      },
      {
        path: 'relinkach',
        component: () => import('@/views/plaidRedirect/AchView.vue'),
      },
    ],
  },
  {
    path: '/payment/:payment/callback/:status',
    name: 'payment.callback',
    component: () => import('@/views/PaymentView.vue'),
    beforeEnter(to, from, next) {
      if (to.query.orderKey) {
        const trackingStore = useTrackingStore();
        trackingStore.recordOrderKey(to.query.orderKey);
      }
      paymentEndpoints().paymentCallback(to, next);
    },
  },
  {
    path: '/payment/:payment',
    name: 'payment.payment',
    component: () => import('@/views/PaymentView.vue'),
    async beforeEnter(to, from, next) {
      const pspType = resolvePspTypeForTracking(to);
      trackPaymentStarted({ psp_type: pspType });
      trackPaymentPageViewed({ psp_type: pspType });
      if (!expressCheckoutEnabled()) {
        progressBarActions().updateToComplete(['verification', 'identity']);
      }
      progressBarActions().updateToPending('payment');
      next();
    },
  },
  {
    path: '/coindeposit/*',
    name: 'coindeposit',
    component: () => import('@/views/CoinDepositView.vue'),
    beforeEnter() {
      progressBarActions().updateToComplete(['verification', 'identity']);
      progressBarActions().updateToPending('payment');
    },
  },
  {
    path: '/deposit',
    name: 'deposit',
    component: () => import('@/views/DepositView.vue'),
  },
  {
    path: '/coindeposit/:orderId',
    name: 'coindeposit',
    component: () => import('@/views/CoinDepositView.vue'),
    beforeEnter() {
      progressBarActions().updateToComplete(['verification', 'identity']);
      progressBarActions().updateToPending('payment');
    },
  },
  {
    path: '/status/:orderId/:token?',
    name: 'summary',
    component: () => import('@/views/OrderSummaryView.vue'),
    beforeEnter(to, from, next) {
      const trackingStore = useTrackingStore();
      const sessionStatusStore = useSessionStatusStore();
      const { orderId, token } = to.params;

      // This order is keyed to its Riskified beacon already, so retire the sid here: the next
      // checkout starts clean, and a later load of this page cannot fire a fresh beacon under
      // the sid this order was paired on.
      resetFraudSid();

      trackingStore.recordOrderKey(orderId);
      progressBarActions().updateToComplete(['verification', 'identity', 'payment']);

      // Reset the session-ready promise before the tracking event fires so that
      // trackStatusViewed() can await the handshake that happens in onMounted.
      sessionStatusStore.resetSessionReady();
      trackStatusViewed();

      // Persist the orderId and login token from the email link so subsequent API calls
      // and page refreshes can authenticate without re-reading the URL.
      sessionStorage.setItem('orderId', orderId);

      if (token) {
        sessionStorage.setItem('loginToken', token);
      }

      next();
    },
  },
  {
    path: '/widget',
    name: 'widget',
    component: () => import('@/views/WidgetView.vue'),
    beforeEnter() {
      progressBarActions().resetAllProgress();
    },
  },
  {
    path: '/error/:errorCode?',
    name: 'error',
    component: () => import('@/views/ErrorView.vue'),
    props: (route) => ({
      errorCode: route.params.errorCode || route.query.errorCode || '',
    }),
  },
  {
    path: '/:pathMatch(.*)*', // Updated catch-all route definition
    name: 'not-found',
    redirect: { name: 'error', query: { errorCode: '404' } }, // Redirect to the 'error' route
  },
  {
    path: '/cancel/:orderId',
    name: 'cancel.order',
    component: () => import('@/views/PaymentCoolingView.vue'),
    async beforeEnter(to, from, next) {
      const trackingStore = useTrackingStore();
      trackingStore.recordOrderKey(to.params.orderId);
      await apiHelpers().getRequest(`/cancel/${to.params.orderId}`, () => {
        next(`/status/${to.params.orderId}`);
      });
    },
  },
  {
    path: '/auth/google/callback',
    name: 'auth.google.callback',
    component: () => import('@/views/AuthCallbackView.vue'),
  },
  {
    path: '/recurring/opt-out/:orderId?',
    name: 'recurring.opt-out',
    component: () => import('@/views/RecurringOrderOptOutView.vue'),
  },
];

export default routes;
